import { Checkbox } from "antd";
import React from "react";
import * as Icons from "lucide-react";
import styled from "styled-components";
import { Task } from "src/types/tasks";

interface TaskCardProps {
  task: Task;
  isSelected: boolean;
  onToggle: (task: Task) => void;
}

export const TaskCard: React.FC<TaskCardProps> = ({
  task,
  isSelected,
  onToggle,
}) => {
  const IconComponent = Icons[task.icon as keyof typeof Icons] as any;
  const priceInDollars = (task.current_price_cents / 100).toFixed(2);

  const handleClick = () => {
    onToggle(task);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle(task);
    }
  };

  return (
    <StyledTaskCard
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      role="checkbox"
      aria-checked={isSelected}
      aria-label={`${task.title}, ${task.description}, $${priceInDollars}`}
      isSelected={isSelected}
    >
      <CardHeader>
        <StyledCheckbox checked={isSelected} onChange={handleClick} />
        <IconWrapper isSelected={isSelected}>
          {IconComponent ? (
            <IconComponent size={24} strokeWidth={1.5} />
          ) : (
            <Icons.HelpCircle size={24} strokeWidth={1.5} />
          )}
        </IconWrapper>
      </CardHeader>
      <CardContent>
        <TaskTitle>{task.title}</TaskTitle>
        <TaskDescription>{task.description}</TaskDescription>
      </CardContent>
      <CardFooter>
        <Price>${priceInDollars}</Price>
        <CategoryBadge>{task.category.name}</CategoryBadge>
      </CardFooter>
    </StyledTaskCard>
  );
};

const StyledTaskCard = styled.div<{ isSelected: boolean }>`
  display: flex;
  flex-direction: column;
  padding: 16px;
  border: 1px solid
    ${(props) => (props.isSelected ? "var(--blue-300)" : "#e2e2e2")};
  border-radius: 6px;
  background-color: ${(props) =>
    props.isSelected ? "rgba(37, 62, 198, 0.05)" : "#fff"};
  cursor: pointer;
  transition: all 0.2s ease;
  min-height: 180px;
  box-shadow: ${(props) =>
    props.isSelected
      ? "0 2px 8px rgba(37, 62, 198, 0.15)"
      : "2px 2px 10px rgba(0, 0, 0, 0.05)"};

  &:hover {
    border-color: ${(props) =>
      props.isSelected ? "var(--blue-300)" : "#d3d3d3"};
    box-shadow: ${(props) =>
      props.isSelected
        ? "0 2px 8px rgba(37, 62, 198, 0.25)"
        : "2px 2px 12px rgba(0, 0, 0, 0.1)"};
  }

  &:focus-visible {
    outline: 2px solid var(--blue-300);
    outline-offset: 2px;
  }

  @media screen and (max-width: 500px) {
    min-height: 160px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
`;

const StyledCheckbox = styled(Checkbox)`
  .ant-checkbox-inner {
    border-color: #e2e2e2;
    border-radius: 4px;
    width: 20px;
    height: 20px;
  }

  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: var(--blue-300);
    border-color: var(--blue-300);
  }

  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-wrapper:hover .ant-checkbox-inner {
    border-color: var(--blue-300);
  }

  .ant-checkbox-checked::after {
    border-color: var(--blue-300);
  }
`;

const IconWrapper = styled.div<{ isSelected?: boolean }>`
  color: ${(props) => (props.isSelected ? "var(--blue-300)" : "#666")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s ease;
`;

const CardContent = styled.div`
  flex: 1;
  margin-bottom: 12px;
`;

const TaskTitle = styled.h3`
  margin: 0 0 8px 0;
  font-size: 16px;
  font-weight: 600;
  color: #373744;
`;

const TaskDescription = styled.p`
  margin: 0;
  font-size: 14px;
  color: #666;
  line-height: 1.4;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;

const Price = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: var(--blue-300);
`;

const CategoryBadge = styled.span`
  font-size: 12px;
  padding: 4px 8px;
  background-color: #eefcff;
  color: var(--blue-300);
  border-radius: 4px;
`;
